/**
 * Sexy Hair blog masonry (WEB-6276)
 *
 * Packs the article grid the way sexyhair.com/blog does: each card goes into the
 * SHORTEST column, walking the cards in DOM order. Measured off live — cards
 * 1, 2, 3 share the top row and column 3 is offset lower because card 3 is
 * taller, which is shortest-column packing, not CSS columns.
 *
 * Why not pure CSS:
 *   - `columns: 3` fills column 1 completely before starting column 2, so the
 *     11 newest posts would run down the left edge. That matches neither live
 *     nor newest-first reading order.
 *   - `grid-template-rows: masonry` is not dependably available across the
 *     browsers this storefront serves, so it can't be the only mechanism.
 *
 * No library — a ~70-line custom element, the same posture as
 * sexyhair-slider-dots.js (see themes/CLAUDE.md → carousels).
 *
 * Degradation: without JS, or below the 2-column breakpoint, the grid keeps its
 * plain CSS-grid layout. Nothing here is required for the content to be usable.
 *
 * Column count comes from the `--sexyhair-masonry-columns` custom property so
 * the breakpoints stay in component CSS, per the utility-vs-component split.
 */
class SexyhairBlogMasonry extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.sexyhair-card-grid');
    if (!this.grid) return;

    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);

    // Titles wrap differently once the Typekit face swaps in, which changes card
    // heights — relayout when fonts settle rather than shipping a stale packing.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.layout()).catch(() => {});
    }

    this.layout();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.layout(), 120);
  }

  columns() {
    const raw = getComputedStyle(this.grid).getPropertyValue('--sexyhair-masonry-columns');
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 1;
  }

  reset() {
    this.grid.classList.remove('sexyhair-card-grid--masonry');
    this.grid.style.height = '';
    for (const item of this.grid.children) {
      item.style.position = '';
      item.style.width = '';
      item.style.left = '';
      item.style.top = '';
    }
  }

  layout() {
    const items = Array.from(this.grid.children);
    const cols = this.columns();

    // One column (mobile) needs no packing — hand it back to CSS grid.
    if (cols < 2 || items.length === 0) {
      this.reset();
      return;
    }

    const styles = getComputedStyle(this.grid);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const rowGap = parseFloat(styles.rowGap || styles.gap) || gap;

    // Width must be applied BEFORE measuring: card height depends on how the
    // title wraps at the final column width.
    this.grid.classList.add('sexyhair-card-grid--masonry');
    // clientWidth includes the grid's own padding, and an absolutely-positioned
    // child is offset from the PADDING edge — so measuring with it and starting at
    // left: 0 made the cards span edge-to-edge with no side gutter, and each
    // column ~20px wider than the CSS baseline. Only visible between 750 and
    // ~1300px, where .sexyhair-card-grid carries `padding: 0 30px` and the grid is
    // narrower than its max-width; the tag views (no masonry) kept their inset, so
    // the landing and its categories disagreed.
    const padLeft = parseFloat(styles.paddingLeft) || 0;
    const padRight = parseFloat(styles.paddingRight) || 0;
    const total = this.grid.clientWidth - padLeft - padRight;
    const colWidth = (total - gap * (cols - 1)) / cols;
    for (const item of items) {
      item.style.position = 'absolute';
      item.style.width = `${colWidth}px`;
    }

    // Read every height BEFORE writing any position: interleaving the
    // offsetHeight read with the left/top writes forces one synchronous
    // reflow per card. Batched, the first read triggers a single layout pass
    // (positions don't change an item's height, so reading early is safe).
    const itemHeights = items.map((item) => item.offsetHeight);

    const heights = new Array(cols).fill(0);
    items.forEach((item, i) => {
      let target = 0;
      for (let c = 1; c < cols; c++) {
        if (heights[c] < heights[target]) target = c;
      }
      item.style.left = `${padLeft + target * (colWidth + gap)}px`;
      item.style.top = `${heights[target]}px`;
      heights[target] += itemHeights[i] + rowGap;
    });

    this.grid.style.height = `${Math.max(...heights) - rowGap}px`;
  }
}

if (!customElements.get('sexyhair-blog-masonry')) {
  customElements.define('sexyhair-blog-masonry', SexyhairBlogMasonry);
}
